import React, { useState, useEffect } from 'react';
import { localToday, dateStr, birthdayError } from '../utils/dates';
import { cleanName, cleanNames } from '../utils/names';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc, setDoc, collection, getDocs, query, where, addDoc, serverTimestamp, increment} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { resolveMember } from '../utils/resolveMember';
import { isStaffRoles } from '../utils/profileAccess';
import { isParentOf } from '../utils/athleteLinks';
import { decimalAgeOn } from '../utils/juniors';
import PrivateSessionRequestDialog from '../components/PrivateSessionRequestDialog';
import Layout from '../components/Layout';
import { logError } from '../utils/errorLogger';
import { logEvent } from '../utils/logEvent';
import {
  Box, Typography, Paper, Grid, Chip, Button, TextField,
  MenuItem, Alert, CircularProgress, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  InputAdornment, Tabs, Tab, Divider,
  FormControl, FormLabel, RadioGroup, Radio, FormControlLabel,
  Switch, Autocomplete
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import { getRoleChipsConfig } from '../utils/memberChips';
import { isComped } from '../utils/comp';

const skillGroups = ['', 'Beginner', 'Intermediate', 'Advanced'];

const INDOOR_LEVELS = ['', 'No experience', 'Recreational', 'Middle School', 'JV', 'Varsity', 'Collegiate', 'Professional'];
const BEACH_LEVELS = ['', 'No experience / Beginner', 'Intermediate', 'Advanced', 'Elite'];
const SHIRT_SIZES = [
  { value: 'YXS',  label: 'Youth X-Small' },
  { value: 'YS',   label: 'Youth Small' },
  { value: 'YM',   label: 'Youth Medium' },
  { value: 'S',    label: 'Small Adult' },
  { value: 'M',    label: 'Medium Adult' },
  { value: 'L',    label: 'Large Adult' },
  { value: 'XL',   label: 'X-Large Adult' },
  { value: 'XXL',  label: 'XX-Large Adult' },
];

function beachToSkillGroup(beachExp) {
  const b = (beachExp || '').toLowerCase();
  if (b.includes('no experience') || b.includes('beginner')) return 'Beginner';
  if (b.includes('intermediate')) return 'Intermediate';
  if (b.includes('advanced')) return 'Advanced';
  return '';
}

// Role-chip styling — mirrors Profile.js and People.js so chips look identical across the app.
const ROLE_COLORS = {
  admin: { bg: '#FDEDEC', color: '#922B21' },
  coach: { bg: '#EBF5FB', color: '#1A5276' },
  assistant_coach: { bg: '#EBF5FB', color: '#1A5276' },
  volunteer: { bg: '#F4F6F7', color: '#555' },
  parent: { bg: '#E9F7EF', color: '#1E6B3C' },
  adult_athlete: { bg: '#FEF9E7', color: '#7B4800' },
  junior_athlete: { bg: '#F5EEF8', color: '#6C3483' },
  member: { bg: '#FEF5D7', color: '#7E6010' },
  renter: { bg: '#E0F2F1', color: '#00695C' },
  lead: { bg: '#FDF2E9', color: '#A04000' },
  player: { bg: '#EBF5FB', color: '#21618C' },
};
const ROLE_LABELS = {
  admin: 'Admin', coach: 'Coach', assistant_coach: 'Assistant Coach',
  volunteer: 'Volunteer', parent: 'Parent', adult_athlete: 'Adult Athlete',
  junior_athlete: 'Junior Athlete', member: 'Member', renter: 'Renter', lead: 'Lead', player: 'Player',
};


const STATUS_COLORS = {
  paid: { bg: '#E9F7EF', color: '#1E6B3C' },
  pending: { bg: '#FEF9E7', color: '#7B4800' },
  refunded: { bg: '#F5EEF8', color: '#6C3483' },
  partial_refund: { bg: '#FDEBD0', color: '#7B4800' },
  failed: { bg: '#FDEDEC', color: '#922B21' },
};

const InfoRow = ({ label, value }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>{label}</Typography>
    <Typography variant="body1" sx={{ fontWeight: 500 }}>{value || '—'}</Typography>
  </Grid>
);

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function AthleteProfile() {
  const { athleteId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  // Moving up a monthly plan (1x -> 2x): the quote, then the change.
  const [upgradeQuote, setUpgradeQuote] = useState(null);
  const [upgradeBusy, setUpgradeBusy] = useState(false);
  const [privateOpen, setPrivateOpen] = useState(false);
  const [clinicCredits, setClinicCredits] = useState(0);
  const [makeupCredits, setMakeupCredits] = useState([]);
  const [grantDialog, setGrantDialog] = useState(false);
  const [grantNote, setGrantNote] = useState('');
  const [granting, setGranting] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [attendance, setAttendance] = useState([]);
  // Check-ins that were undone — the session was given back rather than spent.
  const [returnedSessions, setReturnedSessions] = useState([]);
  // Hand edits to the balance — no class behind them, so they only exist here.
  const [balanceAdjustments, setBalanceAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(searchParams.get('edit') === 'true');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [balanceDialog, setBalanceDialog] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [balanceNote, setBalanceNote] = useState('');
  const [editForm, setEditForm] = useState({});
  const [refundDialog, setRefundDialog] = useState(false);
  const [refundPurchase, setRefundPurchase] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);
  const [parentUser, setParentUser] = useState(null);
  // Guardians beyond the first. The co-parent link writes every guardian into
  // the athlete's parentUserIds array; parentUserId only ever holds one, so
  // reading it alone showed a two-parent family as a one-parent family.
  const [coParents, setCoParents] = useState([]);
  const [tab, setTab] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  // Set when the viewer is not staff, the athlete, or one of their parents.
  const [accessDenied, setAccessDenied] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [parentOptions, setParentOptions] = useState([]);
  // School list for the edit form — same source as Register.js and AddPerson.js
  // (settings/schools, managed on the Admin Schools page). Without it this form
  // was a bare text box while every other place an athlete's school is entered
  // offered the list, so edits drifted into spellings the roster couldn't group.
  const [schools, setSchools] = useState([]);
  useEffect(() => {
    getDoc(doc(db, 'settings', 'schools'))
      .then(snap => { if (snap.exists()) setSchools(snap.data().list || []); })
      .catch(e => {
        console.error('Failed to load schools', e);
        logError('AthleteProfile', 'Failed to load schools: ' + e.message);
      });
  }, []);
  // Membership tier assignment (admin)
  const [allTiers, setAllTiers] = useState([]);
  const [linkedUser, setLinkedUser] = useState(null); // { id, ...data } — the user doc this athlete is associated with (if any)
  const [billingEmail, setBillingEmail] = useState('');
  const [billingSaving, setBillingSaving] = useState(false);
  // Seeded from the linked user; without this the box renders empty and saving
  // would clear an address that was already set.
  useEffect(() => { setBillingEmail(linkedUser?.billingEmail || ''); }, [linkedUser?.id, linkedUser?.billingEmail]);
  // Membership management (Stripe-backed members) — hooks stay top-level.
  const [mmBusy, setMmBusy] = useState(false);
  const [mmMsg, setMmMsg] = useState(null);
  const [mmFreezeMonths, setMmFreezeMonths] = useState(1);
  const [userPurchases, setUserPurchases] = useState([]);

  // Membership/user-level purchases (e.g. membership starts) are keyed by
  // userId, not athleteId — load them once the linked user doc is resolved.
  useEffect(() => {
    if (!linkedUser?.id || linkedUser.id.startsWith('pending_')) { setUserPurchases([]); return; }
    getDocs(query(collection(db, 'purchases'), where('userId', '==', linkedUser.id)))
      .then(snap => setUserPurchases(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => setUserPurchases([]));
  }, [linkedUser?.id]);

  const memberAction = async (action, months) => {
    if (!linkedUser?.id) return;
    setMmBusy(true); setMmMsg(null);
    try {
      const fns = getFunctions();
      const fn = httpsCallable(fns, 'manageMembership');
      await fn({ action, months, targetUserId: linkedUser.id });
      const refreshed = await getDoc(doc(db, 'users', linkedUser.id));
      if (refreshed.exists()) setLinkedUser({ id: refreshed.id, ...refreshed.data() });
      setMmMsg({ severity: 'success', text: `${action} complete.` });
    } catch (err) {
      setMmMsg({ severity: 'error', text: err.message || 'Action failed.' });
    } finally { setMmBusy(false); }
  };
  const [tierSaving, setTierSaving] = useState(false);
  // Create Parent dialog (admin) — for creating a pending parent user account
  // when the parent doesn't have an account yet (e.g., restored juniors with missing parent data)
  const [createParentDialog, setCreateParentDialog] = useState(false);
  const [newParentForm, setNewParentForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [creatingParent, setCreatingParent] = useState(false);

  const handleCreateParent = async () => {
    const fn = cleanName(newParentForm.firstName);
    const ln = cleanName(newParentForm.lastName);
    const em = (newParentForm.email || '').trim().toLowerCase();
    const ph = (newParentForm.phone || '').trim();
    if (!fn || !ln || !em) {
      setError('Please enter parent first name, last name, and email.');
      return;
    }
    setCreatingParent(true);
    setError('');
    try {
      // Check if a user with this email already exists
      const existingSnap = await getDocs(query(collection(db, 'users'), where('email', '==', em)));
      const existingReal = existingSnap.docs.find(d => !d.id.startsWith('pending_'));
      let parentDocId;
      if (existingReal) {
        // Already a real user — link to them and add 'parent' role if not already there
        const existingRoles = existingReal.data().roles || [];
        if (!existingRoles.includes('parent')) {
          await updateDoc(doc(db, 'users', existingReal.id), {
            roles: [...existingRoles, 'parent'],
          });
        }
        parentDocId = existingReal.id;
      } else {
        // No user yet — create pending doc with email-based ID (matches AddPerson pattern)
        const pendingId = `pending_${em.replace(/[^a-z0-9]/g, '_')}`;
        await setDoc(doc(db, 'users', pendingId), {
          firstName: fn,
          lastName: ln,
          email: em,
          phone: ph,
          roles: ['parent'],
          createdAt: serverTimestamp(),
          createdByAdmin: true,
          registrationComplete: true,
          pendingLinkEmail: true,
        }, { merge: true });
        parentDocId = pendingId;
      }
      // Update local parent options so the dropdown reflects the new entry immediately
      setParentOptions(prev => {
        const filtered = prev.filter(u => u.id !== parentDocId);
        return [...filtered, { id: parentDocId, firstName: fn, lastName: ln, email: em, phone: ph, roles: ['parent'] }]
          .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
      });
      // Auto-select this parent in the editForm
      setEditForm(p => ({ ...p, parentUserId: parentDocId }));
      setSuccess(`Parent ${fn} ${ln} created. Save the athlete to link them.`);
      setCreateParentDialog(false);
      setNewParentForm({ firstName: '', lastName: '', email: '', phone: '' });
    } catch (err) {
      setError(`Couldn't create parent: ${err.message || 'Unknown error'}`);
    }
    setCreatingParent(false);
  };

  // ── Membership Tier Assignment (admin) ────────────────────────────────────
  // Sets users.membership.tierId on the linked user doc. If no linked user
  // doc exists, creates a pending one keyed by the athlete's email (following
  // the same pending_{emailkey} convention used elsewhere). Pass tierId=null
  // to clear the assignment.
  // Comped athletes attend without spending sessions. The stored balance is
  // left as-is so removing the comp restores whatever they had.
  const [compSaving, setCompSaving] = useState(false);
  const toggleComped = async () => {
    if (!isAdmin) return;
    setCompSaving(true);
    try {
      const next = !isComped(athlete);
      await updateDoc(doc(db, 'athletes', athleteId), { compedSessions: next });
      setAthlete(prev => ({ ...prev, compedSessions: next }));
      setSuccess(next ? 'Marked as comped — check-ins will not use sessions.' : 'Comp removed — check-ins use sessions again.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError('Failed to update comp status.');
    } finally {
      setCompSaving(false);
    }
  };

  // Billing email lives on the linked USER doc, not the athlete. Editable here
  // because People routes any adult athlete to this page rather than the
  // account profile, so the field on UserProfile is unreachable for them.
  const saveBillingEmail = async () => {
    if (!isAdmin || !linkedUser?.id) return;
    setBillingSaving(true);
    try {
      await updateDoc(doc(db, 'users', linkedUser.id), {
        billingEmail: (billingEmail || '').toLowerCase().trim(),
      });
      setLinkedUser(prev => ({ ...prev, billingEmail: (billingEmail || '').toLowerCase().trim() }));
      setSuccess('Billing email saved.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Failed to save billing email.');
    } finally {
      setBillingSaving(false);
    }
  };

  const handleAssignTier = async (tierId) => {
    if (!isAdmin) return;
    setTierSaving(true);
    setError('');
    try {
      // Resolve the target user doc ID
      let targetUserId = linkedUser?.id;
      if (!targetUserId) {
        const em = (athlete?.email || '').trim().toLowerCase();
        if (!em) {
          setError('Cannot assign tier: athlete has no email to link a user account.');
          setTierSaving(false);
          return;
        }
        // Check whether a user with this email already exists before creating pending
        const existingSnap = await getDocs(query(collection(db, 'users'), where('email', '==', em)));
        const realUser = existingSnap.docs.find(d => !d.id.startsWith('pending_'));
        if (realUser) {
          targetUserId = realUser.id;
          setLinkedUser({ id: realUser.id, ...realUser.data() });
        } else {
          // Create pending user doc for this athlete
          const emailKey = em.replace(/[.@]/g, '_');
          targetUserId = `pending_${emailKey}`;
          const pendingRef = doc(db, 'users', targetUserId);
          const pendingSnap = await getDoc(pendingRef);
          if (!pendingSnap.exists()) {
            await setDoc(pendingRef, {
              email: em,
              firstName: athlete?.firstName || '',
              lastName: athlete?.lastName || '',
              phone: athlete?.phone || '',
              roles: [],
              isPending: true,
              createdAt: serverTimestamp(),
            });
          }
          setLinkedUser({ id: targetUserId, email: em, firstName: athlete?.firstName, lastName: athlete?.lastName });
        }
      }

      // Guard against overwriting an active Stripe subscription
      const targetSnap = await getDoc(doc(db, 'users', targetUserId));
      const currentMembership = targetSnap.exists() ? (targetSnap.data().membership || null) : null;
      if (
        tierId &&
        (currentMembership?.stripeSubscriptionId ||
         (currentMembership?.source === 'stripe' && currentMembership?.subscriptionId)) &&
        currentMembership?.tierId !== tierId
      ) {
        const ok = window.confirm(
          'This person has an active Stripe subscription. Overriding with an admin-assigned tier will not cancel the Stripe billing — you must cancel that separately.\n\nProceed anyway?'
        );
        if (!ok) { setTierSaving(false); return; }
      }

      const currentUser = auth.currentUser;
      if (tierId === null || tierId === '') {
        // Clear assignment — keep Stripe-managed fields intact if present
        await updateDoc(doc(db, 'users', targetUserId), {
          'membership.tierId': null,
          'membership.tierName': null,
          'membership.source': null,
          'membership.assignedAt': null,
          'membership.assignedBy': null,
        });
        setSuccess('Tier cleared.');
      } else {
        const tier = allTiers.find(t => t.id === tierId);
        await updateDoc(doc(db, 'users', targetUserId), {
          membership: {
            ...(currentMembership || {}),
            tierId,
            tierName: tier?.name || '',
            source: 'admin_assigned',
            assignedAt: serverTimestamp(),
            assignedBy: currentUser?.uid || null,
          },
        });
        setSuccess(`Tier "${tier?.name || ''}" assigned.`);
      }

      // Refresh linkedUser snapshot so the UI reflects the new tier
      const refreshed = await getDoc(doc(db, 'users', targetUserId));
      if (refreshed.exists()) setLinkedUser({ id: refreshed.id, ...refreshed.data() });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('AthleteProfile', err.message, err.stack);
      setError(`Couldn't assign tier: ${err.message || 'Unknown error'}`);
    }
    setTierSaving(false);
  };

  // Tournament history (results where this athlete appears in athlete_ids)
  const [tournamentHistory, setTournamentHistory] = useState([]);
  const [tournamentHistoryLoading, setTournamentHistoryLoading] = useState(false);
  // Manual purchase recording (admin)
  const [purchaseDialog, setPurchaseDialog] = useState(false);
  const [allPlans, setAllPlans] = useState([]);
  // The current plan's name, for the cancellation notice. allPlans only loads
  // for admins, and a parent is who most needs to read that notice.
  const [currentPlanName, setCurrentPlanName] = useState('');
  const currentPlanId = athlete?.activePlanId || '';
  useEffect(() => {
    if (!currentPlanId) { setCurrentPlanName(''); return; }
    getDoc(doc(db, 'plans', currentPlanId))
      .then(snap => setCurrentPlanName(snap.exists() ? (snap.data().planName || '') : ''))
      .catch(() => setCurrentPlanName(''));
  }, [currentPlanId]);
  const [purchasePlanId, setPurchasePlanId] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(() => localToday());
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [purchaseMethod, setPurchaseMethod] = useState('cash');
  const [purchaseNote, setPurchaseNote] = useState('');
  const [savingPurchase, setSavingPurchase] = useState(false);

  // Undoing a check-in deletes the attendance row, so a returned session
  // leaves no trace in the Sessions tab — the only record is the event written
  // at the time. Its own effect because it needs BOTH the athlete and the
  // viewer's role, which resolve in different places; gating inside the main
  // loader would read isAdmin before it was set.
  //
  // events is admin-read by rule, so a parent sees only the sessions that
  // stand. That is the right thing for them to see.
  useEffect(() => {
    if (!isAdmin || !athleteId) return;
    let alive = true;
    (async () => {
      try {
        // Filtered by athlete only: adding type to the query would need a
        // composite index for a handful of rows per athlete.
        const evSnap = await getDocs(query(
          collection(db, 'events'), where('target.id', '==', athleteId),
        ));
        if (!alive) return;
        const undone = evSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(e => e.type === 'checkin.undone')
          .map(e => {
            // Our own code writes "… for <class> (YYYY-MM-DD)", so the date and
            // class come back out of it. If that wording ever changes, fall
            // back to when the undo happened rather than showing nothing.
            const m = /^Check-in undone for (.*) \((\d{4}-\d{2}-\d{2})\)\s*$/.exec(e.description || '');
            const stamp = e.timestamp?.seconds
              ? new Date(e.timestamp.seconds * 1000).toISOString().slice(0, 10) : '';
            return {
              id: e.id,
              date: m ? m[2] : stamp,
              sessionType: m ? m[1] : (e.description || 'Session'),
              undoneBy: e.actor?.name || e.actor?.email || '',
              _returned: true,
            };
          })
          .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        setReturnedSessions(undone);

        // Hand edits to the balance, from the same pass over this athlete's
        // events. Numbers come from metadata, not the description, so the
        // wording can change without breaking the row.
        const adjusted = evSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(e => e.type === 'balance.adjusted')
          .map(e => {
            const m = e.metadata || {};
            return {
              id: e.id,
              date: e.timestamp?.seconds
                ? new Date(e.timestamp.seconds * 1000).toISOString().slice(0, 10) : '',
              from: typeof m.from === 'number' ? m.from : null,
              balanceAfter: typeof m.to === 'number' ? m.to : null,
              delta: typeof m.delta === 'number' ? m.delta : null,
              note: m.note || '',
              adjustedBy: e.actor?.name || e.actor?.email || '',
              _adjusted: true,
            };
          })
          .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        setBalanceAdjustments(adjusted);
      } catch (e) {
        // Never let this stop the tab rendering the sessions that stand.
        console.warn('returned-session lookup failed:', e.message);
      }
    })();
    return () => { alive = false; };
  }, [isAdmin, athleteId]);

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      import('firebase/firestore').then(async ({ doc: d, getDoc: g, collection: col, getDocs: gds, query: q, where: w }) => {
        const snap = await g(d(db, 'users', user.uid));
        if (snap.exists()) {
          const roles = snap.data().roles || [];
          const admin = roles.includes('admin') || roles.includes('coach') || roles.includes('assistant_coach');
          setIsAdmin(admin);
          if (admin) {
            setCanEdit(true);
            // Load potential parent users (all users) for the parent picker
            try {
              const usersSnap = await gds(col(db, 'users'));
              const users = usersSnap.docs.map(ud => ({ id: ud.id, ...ud.data() }));
              // Sort by last name, then first name
              users.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
              setParentOptions(users);
            } catch (e) {
              console.error('Failed to load parent options:', e);
            }
            // Load active plans for manual purchase recording
            try {
              const plansSnap = await gds(q(col(db, 'plans'), w('active', '==', true)));
              const plans = plansSnap.docs.map(pd => ({ id: pd.id, ...pd.data() }));
              plans.sort((a, b) => (a.planName || '').localeCompare(b.planName || ''));
              setAllPlans(plans);
            } catch (e) {
              console.error('Failed to load plans:', e);
            }
            return;
          }
          // Check if this athlete belongs to the current user (parent)
          const athleteSnap = await g(d(db, 'athletes', athleteId));
          if (athleteSnap.exists()) {
            const parentId = athleteSnap.data().parentUserId;
            if (parentId === user.uid) setCanEdit(true);
            // Also allow adult athlete to edit their own record
            if (athleteSnap.data().email === user.email) setCanEdit(true);
          }
        }
      });
    }
    const fetchData = async () => {
      const docSnap = await getDoc(doc(db, 'athletes', athleteId));
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Staff, the athlete themselves, or a parent — see utils/profileAccess.
        // The athlete is their linked login (userId) or, for older adult
        // records, a matching email, as the edit check above already allows.
        // Parents match on parentUserId or parentUserIds, for the signed-in
        // login and for the account it resolves to.
        const me = auth.currentUser;
        const viewer = me ? await resolveMember(db, me) : null;
        const myIds = [me?.uid, viewer?.uid].filter(Boolean);
        const myEmail = String(viewer?.email || me?.email || '').trim().toLowerCase();
        const allowed = !!me && (
          isStaffRoles(viewer?.data?.roles)
          || (data.userId && myIds.includes(data.userId))
          || (myEmail && String(data.email || '').trim().toLowerCase() === myEmail)
          || myIds.some(id => isParentOf(data, id))
        );
        if (!allowed) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
        setAthlete({ id: athleteId, ...data });

        // Clinic credits live on the linked USERS doc (a tournament no-show may
        // be credited there). Resolve via parentUserId/userId, else by email.
        (async () => {
          try {
            let uSnap = null;
            const uid = data.parentUserId || data.userId;
            if (uid) {
              const d = await getDoc(doc(db, 'users', uid));
              if (d.exists()) uSnap = d;
            }
            if (!uSnap && data.email) {
              const q = await getDocs(query(collection(db, 'users'), where('email', '==', (data.email || '').trim().toLowerCase())));
              if (!q.empty) uSnap = q.docs[0];
            }
            setClinicCredits(uSnap ? (uSnap.data().clinicCredits || 0) : 0);
            // Filtered on athleteId so the rule can authorise it — a query
            // on status alone cannot prove ownership and Firestore would
            // reject the whole thing.
            try {
              const mc = await getDocs(query(collection(db, 'makeupCredits'),
                where('athleteId', '==', athleteId), where('status', '==', 'available')));
              setMakeupCredits(mc.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (e) { console.warn('makeup credit lookup failed:', e); }
          } catch (e) { console.warn('clinic credit lookup failed:', e); }
        })();

        setEditForm({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          nickname: data.nickname || '',
          skillGroup: data.skillGroup || beachToSkillGroup(data.beachExperience || ''),
          schoolName: data.schoolName || '',
          currentGrade: data.currentGrade || '',
          birthday: data.birthday || '',
          graduationYear: data.graduationYear || '',
          shirtSize: data.shirtSize || '',
          allergies: data.allergies || '',
          medicalConditions: data.medicalConditions || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          zip: data.zip || '',
          indoorExperience: data.indoorExperience || '',
          beachExperience: data.beachExperience || '',
          gender: data.gender || '',
          goals: data.goals || '',
          emergencyName: data.emergencyContact?.name || '',
          emergencyPhone: data.emergencyContact?.phone || '',
          emergencyRelation: data.emergencyContact?.relationship || '',
          roles: data.roles || [],
          parentUserId: data.parentUserId || '',
          athleteType: data.athleteType || '',
          isArchived: data.isArchived || false,
          adminNotes: data.adminNotes || '',
        });

        const extraParentIds = (Array.isArray(data.parentUserIds) ? data.parentUserIds : [])
          .filter(id => id && id !== data.parentUserId);
        if (extraParentIds.length) {
          try {
            const snaps = await Promise.all(extraParentIds.map(id => getDoc(doc(db, 'users', id))));
            setCoParents(snaps.filter(snap => snap.exists()).map(snap => ({ id: snap.id, ...snap.data() })));
          } catch (e) {
            logError('AthleteProfile', 'co-parent load: ' + (e.message || String(e)), e.stack || '');
          }
        }

        if (data.parentUserId) {
          const parentDoc = await getDoc(doc(db, 'users', data.parentUserId));
          if (parentDoc.exists()) {
            const parentData = parentDoc.data();
            setParentUser({ id: parentDoc.id, ...parentData });
            // Junior with no photo of their own — use parent's photo as a sensible default
            if (!data.photoURL && parentData.photoURL) {
              setAthlete(prev => prev ? ({ ...prev, photoURL: parentData.photoURL }) : prev);
            }
          }
        } else if (data.athleteType === 'adult') {
          // Adult athlete — look up matching user doc for missing contact info AND photo
          try {
            let ud = null;
            let udId = null; // capture the matched user doc's ID for membership assignment
            // 1. Try direct doc lookup using athleteId as the user doc ID
            const directDoc = await getDoc(doc(db, 'users', athleteId));
            if (directDoc.exists()) { ud = directDoc.data(); udId = directDoc.id; }
            // 2. Try userId field on athlete doc
            if (!ud && data.userId) {
              const userDoc = await getDoc(doc(db, 'users', data.userId));
              if (userDoc.exists()) { ud = userDoc.data(); udId = userDoc.id; }
            }
            // 3. Extract UID from athleteId pattern (ath_{uid}_{timestamp}) and query
            if (!ud) {
              const parts = athleteId.replace(/^ath_/, '').split('_');
              if (parts.length >= 2) {
                const embeddedUid = parts[0];
                const uidSnap = await getDocs(query(collection(db, 'users'), where('uid', '==', embeddedUid)));
                if (!uidSnap.empty) { ud = uidSnap.docs[0].data(); udId = uidSnap.docs[0].id; }
              }
            }
            // 4. Try by athlete's own email
            if (!ud && data.email) {
              const emailSnap = await getDocs(query(collection(db, 'users'), where('email', '==', data.email)));
              if (!emailSnap.empty) { ud = emailSnap.docs[0].data(); udId = emailSnap.docs[0].id; }
            }
            if (ud) {
              if (udId) setLinkedUser({ id: udId, ...ud });
              setAthlete(prev => ({
                ...prev,
                email: prev.email || ud.email || '',
                phone: prev.phone || ud.phone || '',
                address: prev.address || ud.address || '',
                city: prev.city || ud.city || '',
                state: prev.state || ud.state || '',
                zip: prev.zip || ud.zip || '',
                // Always pull photoURL from user doc if athlete doc doesn't have one
                photoURL: prev.photoURL || ud.photoURL || '',
              }));
              setEditForm(prev => ({
                ...prev,
                email: prev.email || ud.email || '',
                phone: prev.phone || ud.phone || '',
                address: prev.address || ud.address || '',
                city: prev.city || ud.city || '',
                state: prev.state || ud.state || '',
                zip: prev.zip || ud.zip || '',
              }));
            }
          } catch(e) { console.warn('User lookup failed:', e); }
        }
      }

      const [purchaseSnap, attSnap] = await Promise.all([
        getDocs(query(collection(db, 'purchases'), where('athleteId', '==', athleteId))),
        getDocs(query(collection(db, 'attendance'), where('athleteId', '==', athleteId))),
      ]);

      const purchaseList = purchaseSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.purchaseDate?.seconds || 0) - (a.purchaseDate?.seconds || 0));
      setPurchases(purchaseList);

      // Drop sessions whose class was canceled — that class never happened, so
      // it isn't attendance. Records whose class doc is gone are kept, since we
      // can't tell and hiding them would lose history.
      const attAll = attSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      let canceledClassIds = new Set();
      try {
        const ids = [...new Set(attAll.map(a => a.classId).filter(Boolean))];
        const snaps = await Promise.all(ids.map(id => getDoc(doc(db, 'classes', id))));
        canceledClassIds = new Set(snaps
          .filter(s => s.exists() && (s.data().status || '').toLowerCase() === 'canceled')
          .map(s => s.id));
      } catch (e) { /* on lookup failure show everything rather than nothing */ }
      const attList = attAll
        .filter(a => !canceledClassIds.has(a.classId))
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setAttendance(attList);

      setLoading(false);
    };
    fetchData();
  }, [athleteId]);

  // One list, newest first: sessions that were spent and sessions that were
  // given back. A returned session is still something that happened to this
  // athlete, and reading the two apart would hide that a class was attended
  // and then credited.
  const sessionHistory = [...attendance, ...returnedSessions, ...balanceAdjustments]
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  const handleSave = async () => {
    const badBirthday = birthdayError(editForm.birthday, { athleteType: editForm.athleteType });
    if (badBirthday) { setError(badBirthday + '.'); return; }
    setSaving(true);
    setError('');
    try {
      const updateData = {
        ...editForm,
        roles: editForm.roles || [],
        emergencyContact: {
          name: editForm.emergencyName,
          phone: editForm.emergencyPhone,
          relationship: editForm.emergencyRelation,
        },
      };
      // Normalize graduationYear: empty string -> null, valid number string -> number
      if (updateData.graduationYear === '' || updateData.graduationYear === undefined) {
        updateData.graduationYear = null;
      } else if (typeof updateData.graduationYear === 'string') {
        const parsed = parseInt(updateData.graduationYear, 10);
        updateData.graduationYear = isNaN(parsed) ? null : parsed;
      }
      // Normalize birthday: empty string -> null
      if (updateData.birthday === '' || updateData.birthday === undefined) {
        updateData.birthday = null;
      }
      const cleaned = cleanNames(updateData);
      await updateDoc(doc(db, 'athletes', athleteId), cleaned);
      setAthlete(prev => ({ ...prev, ...cleaned }));

      // Carry a rename back to the account, so the two names cannot drift
      // whichever screen the correction is made on.
      //
      // Keyed on athlete.userId, which means "this athlete record IS that
      // account" — never on parentUserId, which means "that account is this
      // child's parent". Renaming a junior must not rename their mother.
      if (athlete?.userId && (cleaned.firstName || cleaned.lastName)) {
        try {
          const nameUpdates = {};
          if (cleaned.firstName) nameUpdates.firstName = cleaned.firstName;
          if (cleaned.lastName) nameUpdates.lastName = cleaned.lastName;
          await updateDoc(doc(db, 'users', athlete.userId), nameUpdates);
        } catch (e) {
          // Non-fatal: the athlete record is already saved, and a non-admin
          // editing their own record cannot write the user doc from here.
          console.warn('Name sync to user doc failed:', e?.message || e);
        }
      }
      // Reload parent info if parentUserId changed
      if (editForm.parentUserId) {
        try {
          const pSnap = await getDoc(doc(db, 'users', editForm.parentUserId));
          if (pSnap.exists()) setParentUser({ id: pSnap.id, ...pSnap.data() });
        } catch {}
      } else {
        setParentUser(null);
      }
      setEditing(false);
      setSuccess('Profile updated successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('AthleteProfile', err.message || String(err), err.stack || '');
      setError('Failed to save. Please try again.');
    }
    setSaving(false);
  };

  const handleBalanceUpdate = async () => {
    const bal = parseInt(newBalance);
    if (isNaN(bal)) { setError('Please enter a valid session balance.'); return; }
    const from = athlete.sessionBalance || 0;
    const note = balanceNote.trim();
    if (bal === from && !note) { setBalanceDialog(false); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'athletes', athleteId), { sessionBalance: bal });
      setAthlete(prev => ({ ...prev, sessionBalance: bal }));

      // A hand edit has no class behind it, so the event log is the only
      // record of who changed the balance and why. Fire-and-forget, like every
      // other logEvent call — logging must not fail the edit that just landed.
      const delta = bal - from;
      logEvent('balance.adjusted', {
        target: {
          id: athleteId,
          email: athlete.email || '',
          name: `${athlete.firstName || ''} ${athlete.lastName || ''}`.trim(),
        },
        description: `Session balance ${from} → ${bal} (${delta >= 0 ? '+' : ''}${delta})`,
        metadata: { from, to: bal, delta, note },
      });
      // Show it in the Sessions tab now; the logged row replaces this on reload.
      setBalanceAdjustments(prev => [{
        id: `pending-${Date.now()}`,
        date: localToday(),
        from,
        balanceAfter: bal,
        delta,
        note,
        adjustedBy: auth.currentUser?.displayName || auth.currentUser?.email || '',
        _adjusted: true,
      }, ...prev]);

      setBalanceDialog(false);
      setNewBalance('');
      setBalanceNote('');
      setSuccess('Session balance updated.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('AthleteProfile', err.message || String(err), err.stack || '');
      setError('Failed to update balance.');
    }
    setSaving(false);
  };

  const openRefundDialog = (purchase) => {
    setRefundPurchase(purchase);
    setRefundAmount(String(purchase.amountPaid));
    setRefundReason('');
    setError('');
    setRefundDialog(true);
  };

  const handleRefund = async () => {
    if (!refundAmount || parseFloat(refundAmount) <= 0) { setError('Please enter a valid refund amount.'); return; }
    // What is LEFT, not what was paid: partial refunds accumulate, and the
    // server caps on the remainder, so checking against amountPaid here would
    // let the dialog offer a figure the callable then refuses.
    const remaining = Math.round(((refundPurchase.amountPaid || 0) - (refundPurchase.refundAmount || 0)) * 100) / 100;
    if (parseFloat(refundAmount) > remaining) {
      setError(`Refund cannot exceed what is left on this purchase ($${remaining.toFixed(2)}).`);
      return;
    }
    setRefunding(true);
    setError('');
    try {
      const functions = getFunctions();
      const processRefund = httpsCallable(functions, 'processRefund');
      await processRefund({ purchaseId: refundPurchase.id, amount: parseFloat(refundAmount), reason: refundReason });
      const newStatus = parseFloat(refundAmount) >= refundPurchase.amountPaid ? 'refunded' : 'partial_refund';
      setPurchases(prev => prev.map(p => p.id === refundPurchase.id
        ? { ...p, paymentStatus: newStatus, refundAmount: parseFloat(refundAmount) } : p));
      setRefundDialog(false);
      setRefundPurchase(null);
      setSuccess('Refund processed. Please adjust the session balance manually if needed.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      logError('AthleteProfile', err.message || String(err), err.stack || '');
      setError('Refund failed: ' + (err.message || 'Unknown error'));
    }
    setRefunding(false);
  };

  // Manual purchase recording (admin)
  const openPurchaseDialog = () => {
    setPurchasePlanId('');
    setPurchaseDate(localToday());
    setPurchaseAmount('');
    setPurchaseMethod('cash');
    setPurchaseNote('');
    setError('');
    setPurchaseDialog(true);
  };

  // When a plan is selected, autofill the amount from the plan's price
  const handleSelectPurchasePlan = (planId) => {
    setPurchasePlanId(planId);
    const plan = allPlans.find(p => p.id === planId);
    if (plan && typeof plan.price === 'number') {
      setPurchaseAmount(String(plan.price));
    }
  };

  // Load tournament history when the Tournaments tab is opened (and we have an athleteId)
  // Load all membership tiers for admin assignment dropdown
  useEffect(() => {
    if (!isAdmin) return;
    if (athlete && athlete.athleteType !== 'adult') return; // tier assignment only for adults
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'membershipTiers'));
        if (cancelled) return;
        const tiers = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        setAllTiers(tiers);
      } catch (e) {
        console.warn('Failed to load membership tiers:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, athlete?.athleteType]);

  useEffect(() => {
    if (tournamentHistory.length > 0) return; // cache for the session
    let cancelled = false;
    (async () => {
      setTournamentHistoryLoading(true);
      try {
        const { collection: col, getDocs: gds } = await import('firebase/firestore');
        const snap = await gds(col(db, 'tournamentResults'));
        const matches = [];
        for (const d of snap.docs) {
          const data = d.data();
          const tournamentId = data.tournament_id || d.id;
          const tournamentName = data.tournament_name || '';
          const sourceUrl = data.source_url || '';
          const divisions = data.divisions || {};
          for (const [divName, divObj] of Object.entries(divisions)) {
            for (const standing of (divObj.standings || [])) {
              if ((standing.athlete_ids || []).includes(athleteId)) {
                // Determine partner from matched_players (if any)
                const partner = (standing.matched_players || []).find(p => p.athlete_id !== athleteId);
                matches.push({
                  tournament_id: tournamentId,
                  tournament_name: tournamentName,
                  date: divObj.date || '',
                  division: divName,
                  team_name: standing.team_name,
                  partner_name: partner ? `${partner.first_name || ''} ${partner.last_name || ''}`.trim() : '',
                  place: standing.place,
                  rank: standing.rank,
                  wins: standing.wins,
                  losses: standing.losses,
                  earned_points: standing.earned_points || 0,
                  status: divObj.status || '',
                  source_url: sourceUrl,
                });
              }
            }
          }
        }
        // Sort newest first
        matches.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        if (!cancelled) setTournamentHistory(matches);
      } catch (err) {
        console.error('Failed to load tournament history:', err);
      } finally {
        if (!cancelled) setTournamentHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, athleteId, tournamentHistory.length]);

  const handleManualPurchase = async () => {
    if (!purchasePlanId) { setError('Please select a plan.'); return; }
    if (!purchaseDate) { setError('Please pick a date.'); return; }
    const amt = parseFloat(purchaseAmount);
    if (isNaN(amt) || amt < 0) { setError('Please enter a valid amount.'); return; }
    setSavingPurchase(true);
    setError('');
    try {
      const plan = allPlans.find(p => p.id === purchasePlanId);
      const sessionsAdded = plan?.sessionsIncluded || 0;
      // Create purchase doc
      const purchaseData = {
        athleteId: athleteId,
        athleteName: `${athlete.firstName || ''} ${athlete.lastName || ''}`.trim(),
        planId: purchasePlanId,
        planName: plan?.planName || '',
        planGroup: plan?.planGroup || '',
        sessionsAdded,
        amountPaid: amt,
        paymentMethod: purchaseMethod,
        paymentStatus: 'paid',
        purchaseDate: purchaseDate,
        note: purchaseNote,
        createdBy: 'admin-manual',
        createdAt: new Date(),
      };
      const purchaseRef = await addDoc(collection(db, 'purchases'), purchaseData);
      // Update athlete's session balance
      const currentBalance = athlete.sessionBalance || 0;
      const newBal = currentBalance + sessionsAdded;
      if (sessionsAdded > 0) {
        await updateDoc(doc(db, 'athletes', athleteId), { sessionBalance: increment(sessionsAdded) });
        setAthlete(prev => ({ ...prev, sessionBalance: newBal }));
      }
      // Refresh purchase list
      setPurchases(prev => [{ id: purchaseRef.id, ...purchaseData }, ...prev]);
      setPurchaseDialog(false);
      setSuccess(sessionsAdded > 0
        ? `Purchase recorded. Added ${sessionsAdded} session${sessionsAdded !== 1 ? 's' : ''} to balance.`
        : 'Purchase recorded.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      logError('AthleteProfile', err.message || String(err), err.stack || '');
      setError('Failed to record purchase: ' + (err.message || 'Unknown error'));
    }
    setSavingPurchase(false);
  };

  const formatDate = (val) => {
    if (!val) return '—';
    if (val.seconds) return new Date(val.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (typeof val === 'string') {
      const [y, m, d] = val.split('-');
      if (y && m && d) return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
      return new Date(val).toLocaleDateString();
    }
    return '—';
  };

  const gradYear = (grade) => {
    if (!grade) return '—';
    return String(new Date().getFullYear() + (12 - parseInt(grade)));
  };

  const memberSince = athlete?.memberSince || athlete?.registrationDate;

  if (loading) return (
    <Layout><Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></Layout>
  );

  if (accessDenied) return (
    <Layout><Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ color: '#555' }}>This athlete's page isn't available.</Typography>
      <Typography variant="body2" sx={{ color: '#888', mt: 1 }}>
        Athlete pages can only be viewed by club staff, the athlete, and their parents.
      </Typography>
    </Box></Layout>
  );

  if (!athlete) return (
    <Layout><Box sx={{ p: 3 }}><Typography variant="h6" sx={{ color: '#888' }}>Athlete not found.</Typography></Box></Layout>
  );

  const isJunior = athlete.athleteType?.toLowerCase() === 'junior';

  // The two guardian sources overlap: the legacy guardian2 fields on guardian 1's
  // user doc describe the same person a linked co-parent doc does, whenever the
  // co-parent later got an account of their own. Match on email and show each
  // guardian once, preferring the real user doc — it is the one that can be
  // opened, and the one the rest of the app reads.
  const emailKey = (e) => (e || '').toLowerCase().trim();
  const shownCoParents = coParents.filter(cp =>
    !emailKey(cp.email) || emailKey(cp.email) !== emailKey(parentUser?.email));
  const legacyGuardian2Email = emailKey(parentUser?.guardian2Email || parentUser?.p2Email);
  const showLegacyGuardian2 = Boolean(parentUser?.guardian2FirstName || parentUser?.p2FirstName)
    && !shownCoParents.some(cp => emailKey(cp.email) && emailKey(cp.email) === legacyGuardian2Email);

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, mx: 'auto' }}>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ color: '#1E3A5F' }}>Back</Button>
          {canEdit && (
            <Button
              startIcon={editing ? <SaveIcon /> : <EditIcon />}
              variant="contained"
              onClick={editing ? handleSave : () => setEditing(true)}
              disabled={saving}
              sx={{ backgroundColor: editing ? '#27AE60' : '#1E3A5F', '&:hover': { backgroundColor: editing ? '#1E8449' : '#2E6DA4' } }}
            >
              {editing ? (saving ? 'Saving...' : 'Save Changes') : 'Edit Profile'}
            </Button>
          )}
        </Box>

        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {athlete.isArchived && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This athlete is archived. {isAdmin ? 'Edit profile and toggle the "Archived" switch to restore.' : ''}
          </Alert>
        )}

        {/* Avatar / Name Card */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 3 }}>
          <Avatar src={athlete.photoURL || ''} sx={{ width: 72, height: 72, backgroundColor: '#2E6DA4', fontSize: '1.6rem', flexShrink: 0 }}>
            {!athlete.photoURL && (athlete.firstName?.charAt(0) || '') + (athlete.lastName?.charAt(0) || '')}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>
              {athlete.firstName} {athlete.lastName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              <Chip label={isJunior ? 'Junior' : 'Adult'} size="small" sx={{ backgroundColor: '#DEEAF1', color: '#1E3A5F', fontWeight: 600 }} />
              {athlete.skillGroup && (
                <Chip label={athlete.skillGroup} size="small"
                  color={athlete.skillGroup === 'advanced' ? 'error' : athlete.skillGroup === 'intermediate' ? 'warning' : 'success'} />
              )}
              {athlete.status && <Chip label={athlete.status} size="small" variant="outlined" />}

              {/* Linked user's account roles + tier badge. Uses the same logic as
                  Profile.js and People.js: render all roles, suppress 'member'
                  chip when a tier badge will render in its place. */}
              {linkedUser && (() => {
                const roles = Array.isArray(linkedUser.roles) ? linkedUser.roles : [];
                const { roles: visibleRoles, tierStyle, isMemberActive } =
                  getRoleChipsConfig(linkedUser);
                return (
                  <>
                    {visibleRoles.map(role => (
                      <Chip
                        key={role}
                        label={ROLE_LABELS[role] || role}
                        size="small"
                        sx={{
                          backgroundColor: ROLE_COLORS[role]?.bg || '#f0f0f0',
                          color: ROLE_COLORS[role]?.color || '#555',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    ))}
                    {tierStyle && (
                      <Chip
                        label={tierStyle.label}
                        size="small"
                        sx={{
                          backgroundColor: tierStyle.bg,
                          color: tierStyle.color,
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      />
                    )}
                    {!tierStyle && isMemberActive && !roles.includes('member') && (
                      <Chip
                        label="Member"
                        size="small"
                        sx={{
                          backgroundColor: ROLE_COLORS.member.bg,
                          color: ROLE_COLORS.member.color,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    )}
                  </>
                );
              })()}
            </Box>
          </Box>
          {/* Session Balance */}
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, flexShrink: 0 }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', lineHeight: 1,
              color: isComped(athlete) ? '#8E44AD'
                : (athlete.sessionBalance || 0) > 2 ? '#27AE60'
                : (athlete.sessionBalance || 0) > 0 ? '#E67E22' : '#E74C3C' }}>
              {isComped(athlete) ? 'Comp' : (athlete.sessionBalance || 0)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
              {isComped(athlete) ? 'attends free' : 'sessions remaining'}
            </Typography>
            {String(athlete.athleteType || '').toLowerCase() === 'junior' && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                <a href="/parent-info.pdf" target="_blank" rel="noopener noreferrer"
                  style={{ color: '#1E3A5F' }}>Parent info (PDF)</a>
              </Typography>
            )}
            <Button size="small" variant="text" onClick={() => setPrivateOpen(true)}
              sx={{ mt: 0.5, p: 0, minWidth: 0, fontSize: '0.72rem', color: '#6C3483', textTransform: 'none' }}>
              Book a private lesson
            </Button>
            <PrivateSessionRequestDialog open={privateOpen} onClose={() => setPrivateOpen(false)} athleteId={athlete.id} />
            {isAdmin && (
              <>
                {!isComped(athlete) && (
                  <Button size="small" variant="outlined" onClick={() => { setNewBalance(String(athlete.sessionBalance || 0)); setBalanceNote(''); setBalanceDialog(true); }}
                    sx={{ mt: 0.5, borderColor: '#1E3A5F', color: '#1E3A5F', fontSize: '0.7rem' }}>
                    Update
                  </Button>
                )}
                <Button size="small" variant="text" disabled={compSaving}
                  onClick={toggleComped}
                  sx={{ mt: 0.5, display: 'block', ml: 'auto', color: '#8E44AD', fontSize: '0.7rem' }}>
                  {isComped(athlete) ? 'Remove comp' : 'Mark comped'}
                </Button>
              </>
            )}
            {clinicCredits > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1, color: '#8E44AD' }}>
                  {clinicCredits}
                </Typography>
                <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                  clinic credit{clinicCredits === 1 ? '' : 's'}
                </Typography>
              </Box>
            )}
            {/* Makeup credits — practices the club owes for weeks it could not
                deliver. Separate from sessionBalance on purpose: that resets to
                the plan allowance every renewal, so a credit kept there would
                be erased by the next invoice. */}
            {(makeupCredits.length > 0 || isAdmin) && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1, color: '#1E6B3C' }}>
                  {makeupCredits.length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                  makeup credit{makeupCredits.length === 1 ? '' : 's'}
                </Typography>
                {isAdmin && (
                  <Button size="small" variant="text" disabled={granting}
                    onClick={() => { setGrantNote(''); setGrantDialog(true); }}
                    sx={{ mt: 0.5, display: 'block', ml: 'auto', color: '#1E6B3C', fontSize: '0.7rem' }}>
                    Grant one
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Paper>

        {/* Granting by hand. The trigger covers weeks the club failed to
            deliver; this covers everything else — a family emergency, a
            goodwill gesture — which is a judgement call, not a rule. */}
        <Dialog open={grantDialog} onClose={() => !granting && setGrantDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Grant a makeup credit</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
              One extra practice for {athlete?.firstName}, usable above their weekly allowance.
              Credits for cancelled practices are granted automatically — this is for everything else.
            </Typography>
            <TextField fullWidth size="small" label="Why (optional)" value={grantNote}
              onChange={e => setGrantNote(e.target.value)}
              placeholder="Family emergency, goodwill, missed for illness…" />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGrantDialog(false)} disabled={granting}>Cancel</Button>
            <Button variant="contained" disabled={granting} onClick={async () => {
              setGranting(true);
              try {
                const fn = httpsCallable(getFunctions(), 'grantMakeupCredit');
                await fn({ athleteId, note: grantNote });
                const mc = await getDocs(query(collection(db, 'makeupCredits'),
                  where('athleteId', '==', athleteId), where('status', '==', 'available')));
                setMakeupCredits(mc.docs.map(d => ({ id: d.id, ...d.data() })));
                setGrantDialog(false);
              } catch (e) {
                logError('AthleteProfile', e.message, e.stack);
                alert('Could not grant the credit: ' + e.message);
              }
              setGranting(false);
            }}>Grant</Button>
          </DialogActions>
        </Dialog>

        {/* Tabs */}
        <Paper elevation={2} sx={{ borderRadius: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #eee', px: 2 }}
            variant="scrollable" scrollButtons="auto">
            <Tab label="Contact Info" />
            <Tab label="Athlete Details" />
            <Tab label="Sessions" />
            <Tab label="Purchases" />
            <Tab label="Tournaments" />
          </Tabs>

          <Box sx={{ p: 3 }}>

            {/* TAB 0: Contact Info */}
            <TabPanel value={tab} index={0}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Contact Information</Typography>
              {editing ? (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField fullWidth label="First Name" value={editForm.firstName || ''} onChange={e => setEditForm(p => ({ ...p, firstName: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField fullWidth label="Last Name" value={editForm.lastName || ''} onChange={e => setEditForm(p => ({ ...p, lastName: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField fullWidth label="Nickname" value={editForm.nickname || ''} onChange={e => setEditForm(p => ({ ...p, nickname: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="Email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="Phone" value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth label="Street Address" value={editForm.address} onChange={e => setEditForm(p => ({ ...p, address: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 5 }}>
                    <TextField fullWidth label="City" value={editForm.city} onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField fullWidth label="State" value={editForm.state} onChange={e => setEditForm(p => ({ ...p, state: e.target.value }))} inputProps={{ maxLength: 2 }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField fullWidth label="ZIP" value={editForm.zip} onChange={e => setEditForm(p => ({ ...p, zip: e.target.value }))} />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <InfoRow label="Email" value={athlete.email || (isJunior ? parentUser?.email : null)} />
                  <InfoRow label="Phone" value={athlete.phone || (isJunior ? parentUser?.phone : null)} />
                  <InfoRow label="Address" value={athlete.address ? `${athlete.address}, ${athlete.city}, ${athlete.state} ${athlete.zip}` : (isJunior && parentUser?.address ? `${parentUser.address}, ${parentUser.city}, ${parentUser.state} ${parentUser.zip}` : null)} />
                </Grid>
              )}

              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Medical & Emergency Contact</Typography>
              {editing ? (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="Emergency Contact Name" value={editForm.emergencyName} onChange={e => setEditForm(p => ({ ...p, emergencyName: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="Emergency Contact Phone" value={editForm.emergencyPhone} onChange={e => setEditForm(p => ({ ...p, emergencyPhone: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="Relationship" value={editForm.emergencyRelation} onChange={e => setEditForm(p => ({ ...p, emergencyRelation: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="Allergies" value={editForm.allergies} onChange={e => setEditForm(p => ({ ...p, allergies: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth label="Medical Conditions" value={editForm.medicalConditions} onChange={e => setEditForm(p => ({ ...p, medicalConditions: e.target.value }))} />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <InfoRow label="Emergency Contact" value={athlete.emergencyContact?.name} />
                  <InfoRow label="Emergency Phone" value={athlete.emergencyContact?.phone} />
                  <InfoRow label="Relationship" value={athlete.emergencyContact?.relationship} />
                  <InfoRow label="Allergies" value={athlete.allergies} />
                  <InfoRow label="Medical Conditions" value={athlete.medicalConditions} />
                </Grid>
              )}

              {isJunior && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Parent / Guardian</Typography>
                  {parentUser ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {/* Parent / Guardian 1 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                        <Avatar sx={{ width: 40, height: 40, backgroundColor: '#2E6DA4', fontSize: '0.9rem' }}>
                          {(parentUser.firstName?.charAt(0) || '') + (parentUser.lastName?.charAt(0) || '')}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>{parentUser.firstName} {parentUser.lastName}</Typography>
                            {parentUser.relationship && <Typography variant="caption" sx={{ color: '#888' }}>({parentUser.relationship})</Typography>}
                          </Box>
                          <Typography variant="body2" sx={{ color: '#888' }}>{parentUser.email}</Typography>
                          {parentUser.phone && <Typography variant="body2" sx={{ color: '#888' }}>{parentUser.phone}</Typography>}
                        </Box>
                        <Button size="small" variant="outlined" onClick={() => navigate(`/people/${athlete.parentUserId}`)}
                          sx={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}>View Profile</Button>
                      </Box>
                      {/* Parent / Guardian 2 */}
                      {showLegacyGuardian2 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                          <Avatar sx={{ width: 40, height: 40, backgroundColor: '#27AE60', fontSize: '0.9rem' }}>
                            {((parentUser.guardian2FirstName || parentUser.p2FirstName)?.charAt(0) || '') + ((parentUser.guardian2LastName || parentUser.p2LastName)?.charAt(0) || '')}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>{parentUser.guardian2FirstName || parentUser.p2FirstName} {parentUser.guardian2LastName || parentUser.p2LastName}</Typography>
                              {parentUser.p2Relationship && <Typography variant="caption" sx={{ color: '#888' }}>({parentUser.p2Relationship})</Typography>}
                            </Box>
                            <Typography variant="body2" sx={{ color: '#888' }}>{parentUser.guardian2Email || parentUser.p2Email}</Typography>
                            {(parentUser.guardian2Phone || parentUser.p2Phone) && <Typography variant="body2" sx={{ color: '#888' }}>{parentUser.guardian2Phone || parentUser.p2Phone}</Typography>}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ) : shownCoParents.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#aaa' }}>No parent account linked.</Typography>
                  ) : null}
                  {shownCoParents.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: parentUser ? 1.5 : 0 }}>
                      {shownCoParents.map(cp => (
                        <Box key={cp.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                          <Avatar sx={{ width: 40, height: 40, backgroundColor: '#27AE60', fontSize: '0.9rem' }}>
                            {(cp.firstName?.charAt(0) || '') + (cp.lastName?.charAt(0) || '')}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>{cp.firstName} {cp.lastName}</Typography>
                              {cp.relationship && <Typography variant="caption" sx={{ color: '#888' }}>({cp.relationship})</Typography>}
                            </Box>
                            <Typography variant="body2" sx={{ color: '#888' }}>{cp.email}</Typography>
                            {cp.phone && <Typography variant="body2" sx={{ color: '#888' }}>{cp.phone}</Typography>}
                          </Box>
                          <Button size="small" variant="outlined" onClick={() => navigate(`/people/${cp.id}`)}
                            sx={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}>View Profile</Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}

              {/* Admin: Athlete Settings — parent link, type, archive */}
              {isAdmin && editing && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Athlete Settings (Admin)</Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth select label="Athlete Type" value={editForm.athleteType || ''}
                        onChange={e => setEditForm(p => ({ ...p, athleteType: e.target.value }))}>
                        <MenuItem value="">— Unset —</MenuItem>
                        <MenuItem value="junior">Junior</MenuItem>
                        <MenuItem value="adult">Adult</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Autocomplete
                        options={parentOptions}
                        value={parentOptions.find(u => u.id === editForm.parentUserId) || null}
                        getOptionLabel={(u) => u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() + (u.email ? ` (${u.email})` : '') : ''}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        filterOptions={(opts, { inputValue }) => {
                          const q = inputValue.toLowerCase().trim();
                          if (!q) return opts;
                          return opts.filter(u => {
                            const haystack = [
                              u.firstName, u.lastName, u.displayName,
                              u.email, u.phone,
                              `${u.firstName || ''} ${u.lastName || ''}`,
                              `${u.lastName || ''} ${u.firstName || ''}`,
                              u.id,
                            ].filter(Boolean).join(' ').toLowerCase();
                            // AND-match: all words in query must appear somewhere in haystack
                            return q.split(/\s+/).every(word => haystack.includes(word));
                          });
                        }}
                        onChange={(e, val) => setEditForm(p => ({ ...p, parentUserId: val ? val.id : '' }))}
                        renderInput={(params) => <TextField {...params} label="Parent / Guardian (link to user)" placeholder="Search by name, email, or phone" />}
                        renderOption={(props, u) => (
                          <li {...props} key={u.id}>
                            <Box>
                              <Typography variant="body2">{u.firstName} {u.lastName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {[u.email, u.phone].filter(Boolean).join(' · ') || '—'}
                              </Typography>
                            </Box>
                          </li>
                        )}
                      />
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setCreateParentDialog(true)}
                        sx={{ mt: 0.5, color: '#1E3A5F', fontSize: '0.75rem', textTransform: 'none' }}
                      >
                        + Create new parent account
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormControlLabel
                        control={<Switch checked={!editForm.isArchived} onChange={e => setEditForm(p => ({ ...p, isArchived: !e.target.checked }))} />}
                        label={editForm.isArchived ? 'Archived (switch on to restore)' : 'Active (switch off to archive)'}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Registration Info</Typography>
              <Grid container spacing={2}>
                <InfoRow label="Member Since" value={memberSince ? formatDate(memberSince) : '—'} />
                <InfoRow label="Plan" value={athlete.planName} />
                <InfoRow label="Waivers Signed" value={athlete.waiverSigned ? '✅ Yes' : '⬜ No'} />
                {linkedUser && <InfoRow label="Login Email" value={linkedUser.email} />}
              </Grid>

              {isAdmin && linkedUser && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <TextField
                    size="small" label="Billing email" type="email"
                    value={billingEmail}
                    onChange={e => setBillingEmail(e.target.value)}
                    placeholder={linkedUser.email}
                    sx={{ minWidth: 280 }}
                    error={Boolean(billingEmail) && !/^\S+@\S+\.\S+$/.test(billingEmail)}
                    helperText={Boolean(billingEmail) && !/^\S+@\S+\.\S+$/.test(billingEmail)
                      ? 'Enter a valid email address'
                      : 'Where Stripe sends receipts. Blank = same as login.'} />
                  <Button size="small" variant="outlined" onClick={saveBillingEmail}
                    disabled={billingSaving || (Boolean(billingEmail) && !/^\S+@\S+\.\S+$/.test(billingEmail))}
                    sx={{ mt: 0.5, borderColor: '#1E3A5F', color: '#1E3A5F' }}>
                    {billingSaving ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
              )}

              {isAdmin && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>Roles</Typography>
                  {editing ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {['player', 'lead', 'volunteer'].map(role => {
                        const active = (editForm.roles || []).includes(role);
                        const colors = {
                          player: { bg: '#EBF5FB', color: '#1A5276' },
                          lead: { bg: '#FEF9E7', color: '#7B4800' },
                          volunteer: { bg: '#F4F6F7', color: '#555' },
                        };
                        const labels = { player: 'Player', lead: 'Lead', volunteer: 'Volunteer' };
                        return (
                          <Chip key={role}
                            label={labels[role]}
                            onClick={() => {
                              const current = editForm.roles || [];
                              const next = active ? current.filter(r => r !== role) : [...current, role];
                              setEditForm(p => ({ ...p, roles: next }));
                            }}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: active ? colors[role].bg : '#f5f5f5',
                              color: active ? colors[role].color : '#999',
                              fontWeight: active ? 700 : 400,
                              border: active ? `1px solid ${colors[role].color}` : '1px solid #ddd',
                              '&:hover': { opacity: 0.85 },
                            }}
                          />
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(athlete.roles || []).length > 0
                        ? (athlete.roles || []).map(r => (
                            <Chip key={r} label={r} size="small"
                              sx={{ backgroundColor: '#EBF5FB', color: '#1A5276', fontWeight: 600, fontSize: '0.75rem' }} />
                          ))
                        : <Typography variant="body2" sx={{ color: '#aaa' }}>No roles assigned</Typography>
                      }
                    </Box>
                  )}
                </>
              )}

              {/* Membership Tier Assignment (admin, adult athletes only) */}
              {isAdmin && athlete.athleteType === 'adult' && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>
                    Membership Tier
                  </Typography>
                  {(() => {
                    const currentMembership = linkedUser?.membership || null;
                    const currentTierId = currentMembership?.tierId || '';
                    // Real Stripe memberships carry stripeSubscriptionId (webhook +
                    // migration shape); the legacy source==='stripe' check kept for safety.
                    const isStripeManaged =
                      !!currentMembership?.stripeSubscriptionId ||
                      (currentMembership?.source === 'stripe' && !!currentMembership?.subscriptionId);
                    if (isStripeManaged) {
                      const mem = currentMembership;
                      const fmtD = (v) => {
                        if (!v) return '\u2014';
                        const d = v.toDate ? v.toDate() : new Date(v);
                        return isNaN(d) ? '\u2014' : d.toLocaleDateString();
                      };
                      const isAnnualLike = /annual|year/i.test(mem.tierName || '');
                      const freezeActive = !!mem.freeze;
                      const yearNow = new Date().getFullYear();
                      const freezesUsed = (mem.freezeHistory || []).filter(h => {
                        const t = h.requestedAt?.toDate ? h.requestedAt.toDate() : new Date(h.requestedAt);
                        return t.getFullYear() === yearNow;
                      }).length;
                      const statusChip = mem.cancelAtPeriodEnd
                        ? { label: `Cancels ${fmtD(mem.cancelAt || mem.currentPeriodEnd)}`, bg: '#E67E22' }
                        : mem.status === 'frozen' ? { label: `Frozen until ${fmtD(mem.freeze?.endsAt)}`, bg: '#5D6D7E' }
                        : mem.status === 'trialing' ? { label: 'Active (prepaid)', bg: '#2E86C1' }
                        : mem.status === 'active' ? { label: 'Active', bg: '#27AE60' }
                        : { label: mem.status || 'Unknown', bg: '#C0392B' };
                      return (
                        <Box>
                          {mmMsg && <Alert severity={mmMsg.severity} onClose={() => setMmMsg(null)} sx={{ mb: 1.5 }}>{mmMsg.text}</Alert>}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 700, color: '#1E3A5F' }}>{mem.tierName || 'Membership'}</Typography>
                            <Chip size="small" label={statusChip.label} sx={{ backgroundColor: statusChip.bg, color: 'white', fontWeight: 700 }} />
                            {mem.migratedFrom && <Chip size="small" label="migrated" variant="outlined" />}
                          </Box>
                          <Typography variant="body2" sx={{ color: '#555' }}>
                            Next billing: {fmtD(mem.currentPeriodEnd)} · Regular {mem.weeklyRegularHours ?? 0}h / Prime {mem.weeklyPrimeHours ?? 0}h weekly · Freezes used {freezesUsed}/2
                          </Typography>
                          {freezeActive && (
                            <Typography variant="body2" sx={{ color: '#B9770E', mt: 0.5 }}>
                              Freeze: {fmtD(mem.freeze.startsAt)} → {fmtD(mem.freeze.endsAt)}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 1 }}>
                            Managed by Stripe subscription {mem.stripeSubscriptionId || mem.subscriptionId} — tier changes happen in Stripe, not here.
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            {mem.cancelAtPeriodEnd ? (
                              <Button size="small" variant="contained" color="success" disabled={mmBusy} onClick={() => memberAction('uncancel')}>Undo Cancel</Button>
                            ) : freezeActive ? (
                              <Button size="small" variant="outlined" disabled={mmBusy} onClick={() => memberAction('unfreeze')}>Remove Freeze</Button>
                            ) : (
                              <>
                                {!isAnnualLike && (
                                  <>
                                    <TextField select size="small" label="Months" value={mmFreezeMonths}
                                      onChange={e => setMmFreezeMonths(parseInt(e.target.value, 10))} sx={{ width: 100 }}>
                                      {[1, 2, 3].map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                    </TextField>
                                    <Button size="small" variant="outlined" disabled={mmBusy || freezesUsed >= 2}
                                      onClick={() => memberAction('freeze', mmFreezeMonths)}>Freeze</Button>
                                  </>
                                )}
                                <Button size="small" variant="outlined" color="error" disabled={mmBusy}
                                  onClick={() => { if (window.confirm('Cancel this membership at period end?')) memberAction('cancel'); }}>
                                  Cancel Membership
                                </Button>
                              </>
                            )}
                            {mmBusy && <CircularProgress size={16} />}
                          </Box>
                        </Box>
                      );
                    }
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <TextField
                            select
                            size="small"
                            label="Assigned Tier"
                            value={currentTierId}
                            onChange={(e) => handleAssignTier(e.target.value || null)}
                            disabled={tierSaving}
                            sx={{ minWidth: 260, backgroundColor: '#f5f5f5', borderRadius: 1 }}
                            helperText={
                              isStripeManaged
                                ? `Stripe subscription active (${currentMembership.status || 'unknown status'})`
                                : currentMembership?.source === 'admin_assigned'
                                  ? 'Manually assigned'
                                  : linkedUser?.id
                                    ? 'No tier assigned'
                                    : athlete.email
                                      ? 'No user account yet — one will be created on assignment'
                                      : 'Athlete needs an email before a tier can be assigned'
                            }
                          >
                            <MenuItem value="">— None —</MenuItem>
                            {allTiers.map(t => (
                              <MenuItem key={t.id} value={t.id}>
                                {t.name}
                                {t.adminOnly ? ' (admin-only)' : ''}
                                {t.enabled === false ? ' (disabled)' : ''}
                              </MenuItem>
                            ))}
                          </TextField>
                          {tierSaving && (
                            <Typography variant="caption" sx={{ color: '#888' }}>
                              Saving…
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })()}
                </>
              )}

              {/* Admin / Coach Notes */}
              {isAdmin && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>
                    Notes <Typography component="span" variant="caption" sx={{ color: '#888', fontWeight: 400 }}>(admin/coach only — not visible to parents or athletes)</Typography>
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth multiline minRows={3} maxRows={10}
                      label="Notes"
                      placeholder="Private notes about this athlete (injuries, behavior, preferences, contact history, etc.)"
                      value={editForm.adminNotes || ''}
                      onChange={e => setEditForm(p => ({ ...p, adminNotes: e.target.value }))}
                    />
                  ) : athlete.adminNotes ? (
                    <Box sx={{ backgroundColor: '#FEF9E7', border: '1px solid #F7DC6F', borderRadius: 1, p: 2, whiteSpace: 'pre-wrap' }}>
                      <Typography variant="body2" sx={{ color: '#5D4037' }}>{athlete.adminNotes}</Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#aaa', fontStyle: 'italic' }}>No notes. Click Edit Profile to add.</Typography>
                  )}
                </>
              )}
            </TabPanel>

            {/* TAB 1: Athlete Details */}
            <TabPanel value={tab} index={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Athlete Details</Typography>
              {editing ? (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth select label="Skill Group" value={editForm.skillGroup || ''}
                      onChange={e => setEditForm(p => ({ ...p, skillGroup: e.target.value }))}
                      helperText="Override auto-derived skill group">
                      {skillGroups.map(s => <MenuItem key={s} value={s}>{s || 'Not assigned'}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth select label="Shirt Size" value={editForm.shirtSize || ''} onChange={e => setEditForm(p => ({ ...p, shirtSize: e.target.value }))}>
                      <MenuItem value="">Not set</MenuItem>
                      {SHIRT_SIZES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth select label="Indoor Experience" value={editForm.indoorExperience || ''}
                      onChange={e => setEditForm(p => ({ ...p, indoorExperience: e.target.value }))}>
                      {INDOOR_LEVELS.map(l => <MenuItem key={l} value={l}>{l || 'Not set'}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth select label="Beach Experience" value={editForm.beachExperience || ''}
                      onChange={e => { const sg = beachToSkillGroup(e.target.value); setEditForm(p => ({ ...p, beachExperience: e.target.value, skillGroup: sg || p.skillGroup })); }}>
                      {BEACH_LEVELS.map(l => <MenuItem key={l} value={l}>{l || 'Not set'}</MenuItem>)}
                    </TextField>
                  </Grid>
                  {isJunior && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend" sx={{ fontSize: '0.85rem' }}>Gender</FormLabel>
                          <RadioGroup row value={editForm.gender || ''} onChange={e => setEditForm(p => ({ ...p, gender: e.target.value }))}>
                            <FormControlLabel value="Male" control={<Radio size="small" />} label="Male" />
                            <FormControlLabel value="Female" control={<Radio size="small" />} label="Female" />
                            <FormControlLabel value="Not Disclosed" control={<Radio size="small" />} label="Not Disclosed" />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        {/* Same control as Register.js and AddPerson.js: pick
                            from settings/schools, but freeSolo so a school not
                            on the list can still be typed. */}
                        <Autocomplete
                          options={schools}
                          value={editForm.schoolName || null}
                          onChange={(_, newValue) => setEditForm(p => ({ ...p, schoolName: newValue || '' }))}
                          freeSolo
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              label="School Name"
                              onChange={e => setEditForm(p => ({ ...p, schoolName: e.target.value }))}
                            />
                          )}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth select label="Grade" value={editForm.currentGrade} onChange={e => setEditForm(p => ({ ...p, currentGrade: e.target.value }))}>
                          {[...Array(8)].map((_, i) => <MenuItem key={i + 5} value={i + 5}>Grade {i + 5}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth type="date" label="Date of Birth"
                          InputLabelProps={{ shrink: true }}
                          value={editForm.birthday || ''}
                          onChange={e => setEditForm(p => ({ ...p, birthday: e.target.value }))}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth type="number" label="Graduation Year"
                          value={editForm.graduationYear || ''}
                          onChange={e => setEditForm(p => ({ ...p, graduationYear: e.target.value }))}
                          helperText={editForm.currentGrade ? `Calculated from grade: ${new Date().getFullYear() + (12 - parseInt(editForm.currentGrade))}` : 'Optional override'}
                          inputProps={{ min: 2024, max: 2040 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="Goals" multiline rows={2} value={editForm.goals} onChange={e => setEditForm(p => ({ ...p, goals: e.target.value }))} />
                      </Grid>
                    </>
                  )}
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <InfoRow label="Skill Group" value={athlete.skillGroup || beachToSkillGroup(athlete.beachExperience)} />
                  <InfoRow label="Shirt Size" value={SHIRT_SIZES.find(s => s.value === athlete.shirtSize)?.label || athlete.shirtSize} />
                  <InfoRow label="Indoor Experience" value={athlete.indoorExperience} />
                  <InfoRow label="Beach Experience" value={athlete.beachExperience} />
                  {isJunior && (
                    <>
                      <InfoRow label="Gender" value={athlete.gender} />
                      {/* Age only for juniors, where groups and eligibility turn on it. */}
                      <InfoRow label="Date of Birth" value={
                        String(athlete.athleteType || '').toLowerCase() !== 'junior' || decimalAgeOn(athlete.birthday) === null
                        ? formatDate(athlete.birthday)
                        : `${formatDate(athlete.birthday)} (${decimalAgeOn(athlete.birthday).toFixed(1)} years old)`} />
                      <InfoRow label="School" value={athlete.schoolName} />
                      <InfoRow label="Grade" value={athlete.currentGrade ? `Grade ${athlete.currentGrade}` : null} />
                      <InfoRow label="Graduation Year" value={athlete.graduationYear || gradYear(athlete.currentGrade)} />
                      <InfoRow label="Goals" value={athlete.goals} />
                    </>
                  )}
                </Grid>
              )}
            </TabPanel>

            {/* TAB 2: Session History */}
            <TabPanel value={tab} index={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Sessions</Typography>
              {sessionHistory.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#aaa' }}>No session history found.</Typography>
              ) : (
                <Box>
                  {sessionHistory.map((a, i) => (
                    <Box key={a.id} sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      py: 1.5, px: 2, borderRadius: 2,
                      backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white', mb: 0.5
                    }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(a.date)}</Typography>
                        {a._adjusted ? (
                          <>
                            <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                              Balance edited{a.adjustedBy ? ` by ${a.adjustedBy}` : ''}
                              {a.from != null && a.balanceAfter != null ? ` · ${a.from} → ${a.balanceAfter}` : ''}
                            </Typography>
                            {a.note && (
                              <Typography variant="caption" sx={{ color: '#555', display: 'block', fontStyle: 'italic', mt: 0.25 }}>
                                “{a.note}”
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#888', textTransform: 'capitalize' }}>{a.sessionType || '—'}</Typography>
                        )}
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        {a._adjusted ? (
                          <>
                            <Chip label={a.delta > 0 ? `+${a.delta}` : String(a.delta ?? '—')} size="small" sx={{
                              height: 20, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.5px',
                              // A note-only edit leaves the balance alone: neutral, not a loss.
                              backgroundColor: a.delta > 0 ? '#E8F5E9' : a.delta < 0 ? '#FDECEA' : '#EEE',
                              color: a.delta > 0 ? '#2C7A57' : a.delta < 0 ? '#B3261E' : '#555',
                            }} />
                            <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.25 }}>
                              Balance after <strong>{a.balanceAfter ?? '—'}</strong>
                            </Typography>
                          </>
                        ) : a._returned ? (
                          <>
                            <Chip label="Comped" size="small" sx={{
                              height: 20, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.5px',
                              backgroundColor: '#E8F5E9', color: '#2C7A57',
                            }} />
                            <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.25 }}>
                              session returned{a.undoneBy ? ` by ${a.undoneBy}` : ''}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="caption" sx={{ color: '#888' }}>Balance after</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{a.balanceAfter ?? '—'}</Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </TabPanel>

            {/* TAB 3: Purchase History */}
            <TabPanel value={tab} index={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>Purchases</Typography>
                {isAdmin && (
                  <Button variant="contained" size="small" onClick={openPurchaseDialog}
                    sx={{ backgroundColor: '#27AE60', '&:hover': { backgroundColor: '#229954' } }}>
                    + Record Purchase
                  </Button>
                )}
              </Box>
              {purchases.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#aaa' }}>No purchases recorded yet.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Plan</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Sessions</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Amount</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchases.map((p, i) => (
                        <TableRow key={p.id} sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                          <TableCell>{formatDate(p.purchaseDate)}</TableCell>
                          <TableCell>{p.planName || '—'}</TableCell>
                          <TableCell align="center">{p.sessionsAdded || 0}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>${(p.amountPaid || 0).toFixed(2)}</TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                              <Chip label={p.paymentStatus} size="small"
                                sx={{ backgroundColor: STATUS_COLORS[p.paymentStatus]?.bg || '#f0f0f0', color: STATUS_COLORS[p.paymentStatus]?.color || '#555', fontWeight: 600, textTransform: 'capitalize' }} />
                              {p.paymentStatus === 'paid' && (
                                <Button size="small" color="error" startIcon={<AssignmentReturnIcon />} onClick={() => openRefundDialog(p)}>Refund</Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {userPurchases.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>
                    Membership Payments
                    <Typography component="span" variant="caption" sx={{ color: '#888', fontWeight: 400, ml: 1 }}>
                      (account-level — renewals bill in Stripe without records here)
                    </Typography>
                  </Typography>
                  {userPurchases
                    .sort((a, b) => ((b.purchaseDate?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0) - (a.purchaseDate?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0)))
                    .slice(0, 10)
                    .map(p => (
                      <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee' }}>
                        <Typography variant="body2">{p.tierName || p.planName || p.description || p.purchaseType || 'Payment'}</Typography>
                        <Typography variant="body2" sx={{ color: '#555', whiteSpace: 'nowrap', ml: 2 }}>
                          {p.amount != null ? `$${Number(p.amount).toFixed(2)}` : (p.amountPaid != null ? `$${Number(p.amountPaid).toFixed(2)}` : '')}
                          {' '}{(() => { const v = p.purchaseDate || p.createdAt || p.timestamp; if (!v) return ''; const d = v.toDate ? v.toDate() : new Date(v); return isNaN(d) ? '' : d.toLocaleDateString(); })()}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              )}
            </TabPanel>

            {/* TAB 4: Tournament History */}
            <TabPanel value={tab} index={4}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Tournament History</Typography>
              {tournamentHistoryLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
              ) : tournamentHistory.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, color: '#888' }}>
                  <Typography variant="body2">No tournament results yet.</Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Results show up here automatically when imported and matched to this athlete.
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Summary stats */}
                  {(() => {
                    const totalEarned = tournamentHistory.reduce((sum, t) => sum + (t.earned_points || 0), 0);
                    const bestPlace = Math.min(...tournamentHistory.map(t => t.place || 999).filter(p => p > 0));
                    const bestEntry = tournamentHistory.find(t => t.place === bestPlace);
                    return (
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#0D1E35', color: 'white' }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1, textTransform: 'uppercase' }}>
                              Tournaments
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#C9A84C' }}>
                              {tournamentHistory.length}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#0D1E35', color: 'white' }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1, textTransform: 'uppercase' }}>
                              Total Earned Points
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#C9A84C' }}>
                              {totalEarned}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#0D1E35', color: 'white' }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1, textTransform: 'uppercase' }}>
                              Best Finish
                            </Typography>
                            {bestEntry ? (
                              <>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#C9A84C' }}>
                                  {bestPlace}{bestPlace === 1 ? 'st' : bestPlace === 2 ? 'nd' : bestPlace === 3 ? 'rd' : 'th'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                  {bestEntry.division} · {bestEntry.tournament_name}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="h4" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.4)' }}>—</Typography>
                            )}
                          </Paper>
                        </Grid>
                      </Grid>
                    );
                  })()}

                  {/* Results table */}
                  <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f4f6f9' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Tournament</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Division</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Partner</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Place</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Record</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Points</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tournamentHistory.map((t, i) => {
                          const place = t.place || 0;
                          const medalColor = place === 1 ? '#FFD700' : place === 2 ? '#C0C0C0' : place === 3 ? '#CD7F32' : null;
                          return (
                            <TableRow key={i} hover sx={{ cursor: 'pointer' }}
                              onClick={() => window.open(`/tournament/${t.tournament_id}/results`, '_blank')}>
                              <TableCell>
                                <Typography variant="caption">{t.date}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.tournament_name}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip label={t.division} size="small" sx={{ fontSize: '0.7rem' }} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">{t.partner_name || '—'}</Typography>
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Box sx={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  width: 28, height: 28, borderRadius: '50%',
                                  backgroundColor: medalColor || '#f0f2f5',
                                  color: medalColor ? '#0D1E35' : '#666',
                                  fontWeight: 700, fontSize: '0.85rem',
                                }}>
                                  {place || '—'}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center', fontFamily: 'monospace' }}>
                                {t.wins}–{t.losses}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center', fontWeight: 700, color: '#C9A84C' }}>
                                {t.earned_points || 0}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', textAlign: 'right' }}>
                    Click a row to view that tournament's full results.
                  </Typography>
                </>
              )}
            </TabPanel>

          </Box>
        </Paper>

        {/* Cancelled subscription: what the family was told on the phone, in
            writing. The plan runs to its end date and is never charged again;
            the balance left after that is no longer reset by a renewal, so it
            keeps like a punch card, with no weekly limit once the plan is gone. */}
        {(() => {
          const pending = athlete.subscriptionStatus === 'active' && athlete.subscriptionCancelAtPeriodEnd;
          const ended = athlete.subscriptionStatus === 'canceled';
          if (!pending && !ended) return null;
          const fmt = (v) => {
            const d = v?.toDate ? v.toDate() : (v ? new Date(v) : null);
            return d && !isNaN(d)
              ? new Date(`${dateStr(d)}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
              : '';
          };
          const planName = currentPlanName || 'monthly plan';
          const bal = athlete.sessionBalance || 0;
          const left = `${bal} session${bal === 1 ? '' : 's'}`;
          return (
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mt: 3, border: '1px solid #E8DAEF' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6C3483' }}>Subscription</Typography>
                <Chip size="small" sx={{ backgroundColor: pending ? '#E67E22' : '#5D6D7E', color: 'white', fontWeight: 700 }}
                  label={pending
                    ? `Cancelled · ends ${fmt(athlete.subscriptionCancelAt || athlete.subscriptionCurrentPeriodEnd)}`
                    : `Ended ${fmt(athlete.subscriptionCanceledAt)}`} />
              </Box>
              {pending ? (
                <Typography variant="body2" sx={{ color: '#555' }}>
                  The {planName} is cancelled. It stays active until{' '}
                  <strong>{fmt(athlete.subscriptionCancelAt || athlete.subscriptionCurrentPeriodEnd)}</strong> and
                  there will be no further charges. The {left} left stay on the account and don't expire.
                  After that date they can be used any week, like a punch card.
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: '#555' }}>
                  The monthly subscription has ended and there are no further charges.
                  {bal > 0 ? ` The ${left} left don't expire and can be used any week.` : ''}
                  {' '}Session packs can be bought from the plans page whenever more are needed.
                </Typography>
              )}
            </Paper>
          );
        })()}

        {/* Active Subscription */}
        {athlete.subscriptionStatus === 'active' && !athlete.subscriptionCancelAtPeriodEnd && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mt: 3, border: '1px solid #E8DAEF' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6C3483', mb: 1 }}>Active Subscription</Typography>
            <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>Monthly subscription is active. Sessions are added automatically each month.</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant="contained" disabled={upgradeBusy}
              sx={{ backgroundColor: '#6C3483', '&:hover': { backgroundColor: '#5B2C6F' } }}
              onClick={async () => {
                setUpgradeBusy(true); setError('');
                try {
                  const { data } = await httpsCallable(getFunctions(), 'previewPlanUpgrade')({ athleteId: athlete.id });
                  if (!data.options?.length) setError('There is no bigger monthly plan to move to.');
                  else setUpgradeQuote(data);
                } catch (err) { setError(err.message || 'Could not price the upgrade.'); }
                setUpgradeBusy(false);
              }}>
              {upgradeBusy && !upgradeQuote ? 'Checking…' : 'Upgrade plan'}
            </Button>
            <Button variant="outlined" color="error" onClick={async () => {
              if (!window.confirm('Cancel this subscription?')) return;
              try {
                const functions = getFunctions();
                const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
                await cancelSubscription({ subscriptionId: athlete.stripeSubscriptionId, athleteId: athlete.id });
                setAthlete(prev => ({ ...prev, subscriptionStatus: 'canceled', stripeSubscriptionId: null }));
                setSuccess('Subscription canceled successfully.');
                setTimeout(() => setSuccess(''), 3000);
              } catch (err) { setError('Failed to cancel subscription.'); logError('AthleteProfile', err.message || String(err), err.stack || ''); }
            }}>Cancel Subscription</Button>
            </Box>
          </Paper>
        )}

        {/* Upgrade a monthly plan. The price is Stripe's own proration quote, so
            what is shown is what is charged. */}
        <Dialog open={Boolean(upgradeQuote)} onClose={() => !upgradeBusy && setUpgradeQuote(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Upgrade {athlete.firstName}'s plan</DialogTitle>
          <DialogContent>
            {upgradeQuote?.options?.map(o => (
              <Box key={o.planId} sx={{ p: 2, mb: 1.5, borderRadius: 2, backgroundColor: '#F5EEF8' }}>
                <Typography sx={{ fontWeight: 700 }}>
                  {upgradeQuote.current?.planName} → {o.planName}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>${(o.dueNowCents / 100).toFixed(2)} charged now</strong> for the rest of this month,
                  to the card on file.
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {o.extraSessions > 0
                    ? `+${o.extraSessions} session${o.extraSessions === 1 ? '' : 's'} added now, on top of the ${athlete.sessionBalance || 0} already on the account.`
                    : `The ${athlete.sessionBalance || 0} sessions already on the account stay.`}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: '#555' }}>
                  Then ${o.monthlyPrice}/month for {o.sessionsIncluded} sessions
                  {upgradeQuote.renewsAt ? `, from ${new Date(upgradeQuote.renewsAt).toLocaleDateString()}` : ''}.
                </Typography>
                <Button fullWidth variant="contained" disabled={upgradeBusy} sx={{ mt: 1.5, backgroundColor: '#6C3483', '&:hover': { backgroundColor: '#5B2C6F' } }}
                  onClick={async () => {
                    setUpgradeBusy(true); setError('');
                    try {
                      const { data } = await httpsCallable(getFunctions(), 'upgradeSubscriptionPlan')({
                        athleteId: athlete.id, planId: o.planId, prorationDate: upgradeQuote.prorationDate,
                      });
                      setAthlete(prev => ({
                        ...prev, activePlanId: data.planId,
                        sessionBalance: (prev.sessionBalance || 0) + (data.extraSessions || 0),
                      }));
                      setUpgradeQuote(null);
                      setSuccess(`Upgraded to ${data.planName}. $${Number(data.amountPaid).toFixed(2)} charged`
                        + `${data.extraSessions ? `, ${data.extraSessions} session${data.extraSessions === 1 ? '' : 's'} added` : ''}.`);
                    } catch (err) {
                      setError(err.message || 'The upgrade did not go through.');
                      logError('AthleteProfile', `upgrade failed: ${err.message || err}`, err.stack || '');
                    }
                    setUpgradeBusy(false);
                  }}>
                  {upgradeBusy ? 'Upgrading…' : `Upgrade and pay $${(o.dueNowCents / 100).toFixed(2)}`}
                </Button>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUpgradeQuote(null)} disabled={upgradeBusy}>Not now</Button>
          </DialogActions>
        </Dialog>

        {/* Balance Dialog */}
        <Dialog open={balanceDialog} onClose={() => setBalanceDialog(false)}>
          <DialogTitle>Update Session Balance</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>Current balance: <strong>{athlete.sessionBalance || 0}</strong> sessions</Typography>
            <TextField fullWidth label="New Session Balance" type="number" value={newBalance}
              onChange={e => setNewBalance(e.target.value)} autoFocus />
            <TextField fullWidth multiline minRows={2} label="Notes" sx={{ mt: 2 }}
              value={balanceNote} onChange={e => setBalanceNote(e.target.value)}
              placeholder="Why is this changing? e.g. credited a rained-out session"
              helperText="Saved with the change and shown in the Sessions tab." />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBalanceDialog(false)}>Cancel</Button>
            <Button onClick={handleBalanceUpdate} variant="contained" disabled={saving} sx={{ backgroundColor: '#27AE60' }}>
              {saving ? 'Saving...' : 'Update Balance'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Parent Dialog (admin) */}
        <Dialog open={createParentDialog} onClose={() => !creatingParent && setCreateParentDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white' }}>Create Parent Account</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              Enter the parent's contact info. A pending account will be created and linked to this athlete.
              When the parent first signs in with this email (Google or Apple), their account will be activated automatically.
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth required label="First Name"
                  value={newParentForm.firstName}
                  onChange={e => setNewParentForm(p => ({ ...p, firstName: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth required label="Last Name"
                  value={newParentForm.lastName}
                  onChange={e => setNewParentForm(p => ({ ...p, lastName: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth required type="email" label="Email"
                  value={newParentForm.email}
                  onChange={e => setNewParentForm(p => ({ ...p, email: e.target.value }))}
                  helperText="Parent should sign in with this exact email to link their account"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label="Phone (optional)"
                  value={newParentForm.phone}
                  onChange={e => setNewParentForm(p => ({ ...p, phone: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateParentDialog(false)} disabled={creatingParent}>Cancel</Button>
            <Button onClick={handleCreateParent} variant="contained" disabled={creatingParent} sx={{ backgroundColor: '#1E3A5F' }}>
              {creatingParent ? 'Creating…' : 'Create Parent'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Refund Dialog */}
        <Dialog open={refundDialog} onClose={() => setRefundDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#E74C3C', color: 'white' }}>Process Refund</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {refundPurchase && (
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ color: '#888' }}>Original Payment</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{refundPurchase.planName}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#27AE60' }}>${refundPurchase.amountPaid?.toFixed(2)}</Typography>
                <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>
                  Method: {refundPurchase.paymentMethod}
                  {refundPurchase.paymentMethod === 'card' ? ' — Stripe refund will be processed automatically' : ' — Mark as refunded only'}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField fullWidth label="Refund Amount *" type="number" value={refundAmount}
                onChange={e => setRefundAmount(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ min: 0.01, max: refundPurchase?.amountPaid, step: 0.01 }}
                helperText="Enter partial amount for partial refund, or full amount for full refund" />
              <TextField fullWidth label="Reason (optional)" value={refundReason}
                onChange={e => setRefundReason(e.target.value)} placeholder="e.g. Injury, schedule conflict..." multiline rows={2} />
              <Alert severity="warning">Session balance will NOT be adjusted automatically. Please update manually after processing.</Alert>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setRefundDialog(false)}>Cancel</Button>
            <Button onClick={handleRefund} variant="contained" disabled={refunding}
              sx={{ backgroundColor: '#E74C3C', '&:hover': { backgroundColor: '#C0392B' } }}>
              {refunding ? 'Processing...' : 'Process Refund'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Record Purchase Dialog */}
        <Dialog open={purchaseDialog} onClose={() => !savingPurchase && setPurchaseDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#27AE60', color: 'white' }}>Record Manual Purchase</DialogTitle>
          <DialogContent sx={{ pt: '20px !important' }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
              Use this to record purchases made outside Stripe (cash, check, etc.) for <strong>{athlete.firstName} {athlete.lastName}</strong>.
              Current balance: <strong>{athlete.sessionBalance || 0}</strong> sessions.
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth select label="Plan" value={purchasePlanId}
                  onChange={e => handleSelectPurchasePlan(e.target.value)} required>
                  <MenuItem value="">— Select a Plan —</MenuItem>
                  {allPlans.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.planName} — ${p.price}{p.sessionsIncluded ? ` (${p.sessionsIncluded} sessions)` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth type="date" label="Purchase Date" value={purchaseDate}
                  onChange={e => setPurchaseDate(e.target.value)} InputLabelProps={{ shrink: true }} required />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth type="number" label="Amount Paid ($)" value={purchaseAmount}
                  onChange={e => setPurchaseAmount(e.target.value)}
                  helperText="Auto-filled from plan; edit for partial payments" required
                  inputProps={{ step: "0.01", min: "0" }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth select label="Payment Method" value={purchaseMethod}
                  onChange={e => setPurchaseMethod(e.target.value)}>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                  <MenuItem value="venmo">Venmo</MenuItem>
                  <MenuItem value="zelle">Zelle</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label="Note (optional)" value={purchaseNote}
                  onChange={e => setPurchaseNote(e.target.value)}
                  placeholder="e.g. Check #1234, paid at event" />
              </Grid>
              {purchasePlanId && (() => {
                const p = allPlans.find(pl => pl.id === purchasePlanId);
                if (!p?.sessionsIncluded) return null;
                return (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="info">
                      This will add <strong>{p.sessionsIncluded}</strong> session{p.sessionsIncluded !== 1 ? 's' : ''} to the athlete's balance
                      (new total: <strong>{(athlete.sessionBalance || 0) + p.sessionsIncluded}</strong>).
                    </Alert>
                  </Grid>
                );
              })()}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPurchaseDialog(false)} disabled={savingPurchase}>Cancel</Button>
            <Button onClick={handleManualPurchase} variant="contained" disabled={savingPurchase || !purchasePlanId}
              sx={{ backgroundColor: '#27AE60', '&:hover': { backgroundColor: '#229954' } }}>
              {savingPurchase ? 'Saving...' : 'Record Purchase'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Layout>
  );
}

export default AthleteProfile;
